const periodOrder = ["daily", "weekly", "monthly", "yearly"];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatValue(value, suffix = "") {
  if (value == null || Number.isNaN(value)) return "N/A";
  return `${value}${suffix}`;
}

function closedTradesFromDailyRow(r) {
  const tcRaw = Number(r?.trades_count);
  const wi = Number(r?.wins_count) || 0;
  const lo = Number(r?.losses_count) || 0;
  const fromWl = wi + lo;
  const fromTc =
    Number.isFinite(tcRaw) && tcRaw >= 0 ? Math.floor(tcRaw) : 0;
  return Math.max(fromTc, fromWl);
}

function ymdUTC(y, m0, d) {
  return `${y}-${pad2(m0 + 1)}-${pad2(d)}`;
}

function monthKeyUtc(o) {
  return o.y * 12 + o.m0;
}

function utcMonthFromKey(k) {
  return { y: Math.floor(k / 12), m0: k % 12 };
}

function parseYmdParts(s) {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (mo < 0 || mo > 11 || d < 1 || d > 31) return null;
  return { y, m0: mo, d };
}

function monthUtcFromYmd(dateStr) {
  const p = parseYmdParts(dateStr);
  if (!p) return null;
  return { y: p.y, m0: p.m0 };
}

function firstMonthWithTradeActivityUtc(dailyRows) {
  let bestK = null;
  for (const r of dailyRows || []) {
    if (closedTradesFromDailyRow(r) < 1) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k < bestK) bestK = k;
  }
  if (bestK != null) return utcMonthFromKey(bestK);
  for (const r of dailyRows || []) {
    const hasPips =
      (Number(r.pips_positive) || 0) !== 0 ||
      (Number(r.pips_negative) || 0) !== 0;
    const hasPnl = Math.abs(Number(r.pnl_usd) || 0) > 1e-9;
    if (!hasPips && !hasPnl) continue;
    const p = parseYmdParts(r.trading_date);
    if (!p) continue;
    const k = monthKeyUtc({ y: p.y, m0: p.m0 });
    if (bestK == null || k < bestK) bestK = k;
  }
  return bestK != null ? utcMonthFromKey(bestK) : null;
}

export function tableMonthBoundsFromDaily(dailyRows, firstTradingDate) {
  const max = currentUtcMonth();
  const firstFromRows = firstMonthWithTradeActivityUtc(dailyRows);
  const firstFromApi = monthUtcFromYmd(firstTradingDate);
  let min = firstFromRows;
  if (firstFromApi) {
    if (!min || monthKeyUtc(firstFromApi) < monthKeyUtc(min)) {
      min = firstFromApi;
    }
  }
  if (!min) min = max;
  let minK = monthKeyUtc(min);
  const maxK = monthKeyUtc(max);
  if (minK > maxK) minK = maxK;
  return { min: utcMonthFromKey(minK), max };
}

function formatMonthYearUtcLabel(y, m0) {
  const raw = new Date(Date.UTC(y, m0, 1)).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function currentUtcMonth() {
  const n = new Date();
  return { y: n.getUTCFullYear(), m0: n.getUTCMonth() };
}

function weekdayKeysUtcMonth(y, m0) {
  const last = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
  const keys = [];
  for (let day = 1; day <= last; day++) {
    const dt = new Date(Date.UTC(y, m0, day));
    const wd = dt.getUTCDay();
    if (wd !== 0 && wd !== 6) keys.push(ymdUTC(y, m0, day));
  }
  return keys;
}

export function buildPerformanceTableRows(activePeriod, dailyRows, viewMonthUtc) {
  const byDate = new Map((dailyRows || []).map((r) => [r.trading_date, r]));
  const vm = viewMonthUtc || currentUtcMonth();
  const y = vm.y;
  const m0 = vm.m0;

  if (activePeriod === "daily") {
    const keys = weekdayKeysUtcMonth(y, m0);
    const rows = keys.map((date) => {
      const r = byDate.get(date);
      if (r) return { ...r, rowKey: date, periodLabel: date };
      return {
        rowKey: date,
        periodLabel: date,
        trading_date: date,
        pips_positive: null,
        pips_negative: null,
        trades_count: null,
        wins_count: null,
        losses_count: null,
        win_rate_percent: null,
      };
    });
    return { mode: "daily", rows };
  }

  if (activePeriod === "weekly") {
    const last = new Date(Date.UTC(y, m0 + 1, 0)).getUTCDate();
    const rows = [];
    for (let start = 1; start <= last; start += 7) {
      const endD = Math.min(start + 6, last);
      let pPos = 0;
      let pNeg = 0;
      let t = 0;
      let w = 0;
      let l = 0;
      for (let day = start; day <= endD; day++) {
        const dt = new Date(Date.UTC(y, m0, day));
        const wd = dt.getUTCDay();
        if (wd === 0 || wd === 6) continue;
        const r = byDate.get(ymdUTC(y, m0, day));
        if (r) {
          pPos += Number(r.pips_positive) || 0;
          pNeg += Number(r.pips_negative) || 0;
          t += closedTradesFromDailyRow(r);
          w += Number(r.wins_count) || 0;
          l += Number(r.losses_count) || 0;
        }
      }
      const wr = t > 0 ? Number(((w / t) * 100).toFixed(2)) : null;
      rows.push({
        rowKey: `w-${y}-${m0}-${start}-${endD}`,
        periodLabel: `Week ${rows.length + 1} (days ${start}–${endD})`,
        pips_positive: pPos,
        pips_negative: pNeg,
        trades_count: t,
        wins_count: w,
        losses_count: l,
        win_rate_percent: wr,
      });
    }
    return { mode: "weekly", rows };
  }

  if (activePeriod === "monthly") {
    const monthMap = new Map();
    for (const r of dailyRows || []) {
      if (!r?.trading_date) continue;
      const ym = r.trading_date.slice(0, 7);
      if (!monthMap.has(ym)) {
        monthMap.set(ym, { pPos: 0, pNeg: 0, t: 0, w: 0, l: 0 });
      }
      const a = monthMap.get(ym);
      a.pPos += Number(r.pips_positive) || 0;
      a.pNeg += Number(r.pips_negative) || 0;
      a.t += closedTradesFromDailyRow(r);
      a.w += Number(r.wins_count) || 0;
      a.l += Number(r.losses_count) || 0;
    }
    const ymKeys = [...monthMap.keys()].sort();
    const rows = ymKeys.map((ym) => {
      const [yy, mm] = ym.split("-").map(Number);
      const rawLabel = new Date(Date.UTC(yy, mm - 1, 1)).toLocaleDateString(
        "fr-FR",
        { month: "long", year: "numeric" },
      );
      const label = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
      const a = monthMap.get(ym);
      const trades = a.t;
      const wr = trades > 0 ? Number(((a.w / trades) * 100).toFixed(2)) : null;
      return {
        rowKey: ym,
        periodLabel: label,
        pips_positive: a.pPos,
        pips_negative: a.pNeg,
        trades_count: trades,
        wins_count: a.w,
        losses_count: a.l,
        win_rate_percent: wr,
      };
    });
    return { mode: "monthly", rows };
  }

  if (activePeriod === "yearly") {
    const yearMap = new Map();
    for (const r of dailyRows || []) {
      if (!r?.trading_date) continue;
      const yyyy = r.trading_date.slice(0, 4);
      if (!/^\d{4}$/.test(yyyy)) continue;
      if (!yearMap.has(yyyy)) {
        yearMap.set(yyyy, { pPos: 0, pNeg: 0, t: 0, w: 0, l: 0 });
      }
      const a = yearMap.get(yyyy);
      a.pPos += Number(r.pips_positive) || 0;
      a.pNeg += Number(r.pips_negative) || 0;
      a.t += closedTradesFromDailyRow(r);
      a.w += Number(r.wins_count) || 0;
      a.l += Number(r.losses_count) || 0;
    }
    const yearKeys = [...yearMap.keys()].sort();
    const rows = yearKeys.map((yyyy) => {
      const a = yearMap.get(yyyy);
      const trades = a.t;
      const wr = trades > 0 ? Number(((a.w / trades) * 100).toFixed(2)) : null;
      return {
        rowKey: yyyy,
        periodLabel: yyyy,
        pips_positive: a.pPos,
        pips_negative: a.pNeg,
        trades_count: trades,
        wins_count: a.w,
        losses_count: a.l,
        win_rate_percent: wr,
      };
    });
    return { mode: "yearly", rows };
  }

  return { mode: "daily", rows: [] };
}

export {
  periodOrder,
  formatValue,
  closedTradesFromDailyRow,
  firstMonthWithTradeActivityUtc,
  formatMonthYearUtcLabel,
  monthKeyUtc,
  utcMonthFromKey,
};
